import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface SetPresetDurationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  durationMinutes: number | '';
  presetDurations: number[];
  onDurationSelect: (duration: number) => void;
  onCustomDurationChange: (value: string) => void;
  onConfirm: () => void;
  isSubmitting: boolean;
}

export default function SetPresetDurationDialog({
  open,
  onOpenChange,
  durationMinutes,
  presetDurations,
  onDurationSelect,
  onCustomDurationChange,
  onConfirm,
  isSubmitting,
}: SetPresetDurationDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Set Current Preset</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Choose how long this preset should remain active before automatically turning off the
            area.
          </p>

          <div className="grid grid-cols-3 gap-2">
            <Button
              key={0}
              variant={durationMinutes === 0 ? 'default' : 'outline'}
              onClick={() => onDurationSelect(0)}
              disabled={isSubmitting}
              className="col-span-3"
            >
              ∞
            </Button>
            {presetDurations.map((duration) => (
              <Button
                key={duration}
                variant={durationMinutes === duration ? 'default' : 'outline'}
                onClick={() => onDurationSelect(duration)}
                disabled={isSubmitting}
              >
                {duration} min
              </Button>
            ))}
          </div>

          <div className="space-y-2">
            <Label htmlFor="custom-duration">
              Custom duration (minutes)
            </Label>
            <Input
              id="custom-duration"
              type="number"
              min="0"
              value={durationMinutes === '' ? '' : durationMinutes}
              onChange={(e) => onCustomDurationChange(e.target.value)}
              placeholder="Enter minutes (0 to disable auto-off)"
              disabled={isSubmitting}
            />
            <p className="text-xs text-muted-foreground">
              0 minutes will keep the preset active indefinitely.
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={onConfirm}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Set Preset'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
